<div class="app-container" [class.is-mobile]="mobileQuery.matches">
  <mat-toolbar color="primary" class="app-toolbar">
    <div class="app-toolbar-sections">
      <!-- Start: Hamburger + Title -->
      <div class="app-toolbar-start">
        <button mat-icon-button (click)="mainSideNav.toggle()"><mat-icon>menu</mat-icon></button>
        <span class="app-toolbar-title" i18n="@@Select Role">Select Role</span>
      </div>

      <!-- End -->
      <div class="app-toolbar-end">
        <app-active-role></app-active-role>
      </div>
    </div>
  </mat-toolbar>
  <mat-sidenav-container class="app-sidenav-container" (backdropClick)="mainSideNav.close()" [style.marginTop.px]="mobileQuery.matches ? 56 : 0">
    <mat-sidenav opened #mainSideNav [mode]="mobileQuery.matches ? 'over' : 'side'" (keydown.escape)="mainSideNav.close()" [fixedInViewport]="mobileQuery.matches" fixedTopGap="56">
      <mat-nav-list>
        <!--
          `SideNavComponent`'s primary link list is wrapped in
          `*ngIf="startupService.isDataLoaded"`. Before a role is
          picked here, `isDataLoaded` is false, so only the
          always-visible Logout entry at the bottom of the SideNav
          renders — matching `StartupComponent`'s behaviour.
        -->
        <app-side-nav focus="selectRole"></app-side-nav>
      </mat-nav-list>
    </mat-sidenav>
    <mat-sidenav-content class="m2app-dark">
      <div class="sr-shell">
        <div class="sr-card">

          <div class="sr-header">
            <h1 class="sr-title" i18n="@@Select Role">Select Role</h1>
            <div class="sr-subtitle" i18n="@@Choose the role you want to work as">Choose the role you want to work as. Data for that role loads only after you pick.</div>
          </div>

          <!-- ── Primary role buttons ─────────────────────────── -->
          <div class="sr-section">
            <div class="sr-section-title" i18n="@@Your roles">Your roles</div>

            <div class="sr-role-buttons">
              <!--
                Your own user role (matches SideNav first button).
                Hidden when it already IS the active selection —
                `StartupService.selectRole()` no-ops on same-combo
                requests, so a button that would do nothing when
                clicked has no place on a picker page.
              -->
              <button class="sr-role-btn"
                      *ngIf="!isDefaultUserRoleCurrent"
                      (click)="selectDefaultUserRole()"
                      i18n-title="@@Your user role" title="Your user role">
                <mat-icon [matBadge]="startupService.getRoleId() | numberFormat" matBadgeColor="primary">account_box</mat-icon>
                <span class="sr-role-btn-label" i18n="@@My User">My User</span>
              </button>

              <ng-container *ngFor="let roleOption of orderedRoleOptions">
                <button class="sr-role-btn"
                        (click)="selectRoleOption(roleOption.role, roleOption.roleId)"
                        [title]="titleForRoleOption(roleOption)">
                  <mat-icon [matBadge]="roleOption.roleId | numberFormat" matBadgeColor="primary">{{iconFor(roleOption.role)}}</mat-icon>
                  <span class="sr-role-btn-label">{{labelFor(roleOption.role)}}</span>
                </button>
              </ng-container>
            </div>
          </div>

          <!-- ── Organization autocomplete (network / serviceProvider only) ── -->
          <div class="sr-section" *ngIf="shouldShowOrganizationPicker">
            <div class="sr-section-title" i18n="@@Act as a specific organization">Act as a specific organization</div>
            <div class="sr-section-hint" i18n="@@srOrgHint">Search by organization name or Id. Only active organizations under your role are shown.</div>

            <div *ngIf="loadingOrganizations" class="sr-loading">
              <mat-icon>hourglass_empty</mat-icon>
              <span i18n="@@Loading organizations">Loading organizations…</span>
            </div>
            <div *ngIf="!loadingOrganizations && organizationsError" class="sr-error">
              <div class="sr-error-text">{{organizationsError}}</div>
              <ul class="sr-error-list" *ngIf="organizationLoadFailures.length > 0">
                <li *ngFor="let f of organizationLoadFailures">
                  <strong>{{f.label}}</strong>: {{f.message}}
                </li>
              </ul>
              <button mat-stroked-button color="warn"
                      *ngIf="organizationLoadFailures.length > 0"
                      (click)="retryOrganizationLoads()"
                      i18n-title="@@Retry" title="Retry">
                <mat-icon>refresh</mat-icon>
                <span i18n="@@Retry">Retry</span>
              </button>
            </div>

            <mat-form-field class="sr-org-search" appearance="outline" *ngIf="!loadingOrganizations">
              <mat-label i18n="@@Organization">Organization</mat-label>
              <input matInput
                     type="text"
                     [formControl]="organizationSearchControl"
                     [matAutocomplete]="orgAuto"
                     i18n-placeholder="@@searchRoleOrgSearch"
                     placeholder="Search for organization name, organization Id, or primary organization user email">
              <button matSuffix mat-icon-button
                      *ngIf="selectedOrganization || organizationSearchControl.value"
                      (click)="clearSelectedOrganization()"
                      i18n-title="@@Clear" title="Clear">
                <mat-icon>close</mat-icon>
              </button>
              <mat-autocomplete #orgAuto="matAutocomplete"
                                [displayWith]="displayOrganization"
                                (optionSelected)="onOrganizationSelected($event.option.value)">
                <mat-option *ngFor="let org of filteredOrganizations$ | async" [value]="org">
                  <span class="sr-org-name">{{org.name || '(unnamed)'}}</span>
                  <span class="sr-org-id">#{{org.ID | numberFormat}}</span>
                  <span class="sr-org-sp" *ngIf="org.serviceProvider_ID" i18n-title="@@Service Provider" title="Service Provider">
                    <mat-icon>room_service</mat-icon>{{org.serviceProvider_ID | numberFormat}}
                  </span>
                  <!--
                    Primary organization user's email, shown only
                    when the server included it in the row (added
                    2026-08-06 — falls back to hidden for older
                    server builds or organizations with no
                    assigned admin).
                  -->
                  <span class="sr-org-email" *ngIf="org.adminEmail" i18n-title="@@Primary organization user email" title="Primary organization user email">
                    <mat-icon>alternate_email</mat-icon>{{org.adminEmail}}
                  </span>
                </mat-option>
                <mat-option *ngIf="(filteredOrganizations$ | async)?.length === 0" disabled>
                  <span i18n="@@No matching organizations">No matching organizations</span>
                </mat-option>
              </mat-autocomplete>
            </mat-form-field>

            <div class="sr-org-actions" *ngIf="selectedOrganization">
              <button mat-raised-button color="primary"
                      (click)="proceedIntoSelectedOrganization()"
                      i18n-title="@@Proceed into this organization"
                      title="Proceed into this organization">
                <mat-icon>supervised_user_circle</mat-icon>
                <span i18n="@@Proceed">Proceed</span>
                <span class="sr-proceed-name"><span i18n="acting on behalf of|Precedes the organization the operator is about to act as, e.g. &quot;as Acme Press (#42)&quot;.@@as">as</span> {{selectedOrganization.name}} (#{{selectedOrganization.ID | numberFormat}})</span>
              </button>
            </div>
          </div>

        </div>
      </div>
    </mat-sidenav-content>
  </mat-sidenav-container>
</div>


